import { PublicClientApplication, type Configuration } from '@azure/msal-browser';

import type { ConsoleConfig } from './config/consoleConfig';
import { buildMsalConfig } from './authConfig';

/**
 * The process-wide MSAL instance.
 *
 * FD-420 turns this from a construct-at-import `const` into a factory plus an
 * ESM live binding: the value is undefined until `createMsalInstance` runs in
 * `main.tsx`, and every importer (`api/client.ts`, `api/graph.ts`, and
 * `MsalProvider` itself) reads the bound value at CALL time — all of which
 * happens after boot. Consumers therefore did not change.
 *
 * Typed non-nullable on purpose. A consumer reaching this before boot is a
 * boot-order defect, and `undefined.getActiveAccount()` names the line that did
 * it; an optional type would spread null checks across five call sites to
 * describe a state that must never occur.
 */
export let msalInstance!: PublicClientApplication;

/**
 * Create the instance from the runtime console configuration.
 *
 * **ONE instance serves every environment in a fleet**, which looks wrong at
 * first and is correct: `clientId`, `authority` and `scope` are identical across
 * a fleet (FD-ADR-040 §7), and MSAL caches tokens by
 * `(homeAccountId, environment, clientId, realm, scopes)` — none of which is the
 * Manager host. So one cached token serves every environment, and "sign in once,
 * switch environments" is true rather than aspirational. Per-environment
 * instances would mean N sign-ins for one operator.
 *
 * Idempotent-by-replacement rather than guarded: calling twice is a boot-order
 * defect, but silently returning a stale instance built from a previous config
 * would be the harder failure to see.
 */
export function createMsalInstance(cfg: ConsoleConfig): PublicClientApplication {
  const configuration: Configuration = buildMsalConfig(cfg);
  msalInstance = new PublicClientApplication(configuration);
  return msalInstance;
}
